import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
// import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import { 
  RefreshCw, 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Trash2,
  Eye,
  Building2,
  User
} from 'lucide-react';
import { AuthErrorState } from '@/components/AuthErrorState';
import { safeString } from '@/components/safeRender';
import ClienteFornecedorModal from '@/components/ClientesFornecedores/ClienteFornecedorModal';
import type { ClienteFornecedorWithDetails } from '@/types';
import { useAuthStore } from '@/stores/authStore';

export const ClientesFornecedoresPageSimple: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const isOperador = user?.role === 'OPERADOR';
  const pageTitle = isOperador ? 'Meus clientes' : 'Clientes/Fornecedores';
  const pageSubtitle = isOperador
    ? 'Clientes da sua carteira de cobrança'
    : 'Gerencie clientes e fornecedores do sistema';

  // Sincronizar busca da URL (topbar) com o estado local
  useEffect(() => {
    const q = searchParams.get('search');
    if (q != null && q !== '') setSearchTerm(q);
  }, [searchParams]);

  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Modal do cliente (funil, etapa, status, títulos)
  const [selectedCliente, setSelectedCliente] = useState<ClienteFornecedorWithDetails | null>(null);
  const [isClienteModalOpen, setIsClienteModalOpen] = useState(false);
  const autoOpenedSearchRef = useRef<string | null>(null);

  // Estados para paginação (servidor)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Estados para ordenação (página atual)
  const [sortField, setSortField] = useState<string>('nome');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Estados para filtros por coluna (página atual)
  const [columnFilters, setColumnFilters] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    empresa: '',
    status: 'all'
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, companyFilter, itemsPerPage]);

  const { data: companiesData } = useQuery({
    queryKey: ['companies', 'active', 'clientes-list'],
    queryFn: async () => {
      const response = await api.get('/companies?isActive=true&limit=500');
      const payload = response.data?.data;
      return Array.isArray(payload) ? payload : (payload?.companies ?? []);
    },
  });

  const companies = useMemo(
    () =>
      (Array.isArray(companiesData) ? companiesData : []).map((c: { id: string; name: string }) => ({
        id: c.id,
        name: c.name,
      })),
    [companiesData]
  );

  const {
    data: clientesData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [
      'clientes-fornecedores-simple',
      debouncedSearch,
      companyFilter,
      statusFilter,
      currentPage,
      itemsPerPage,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', String(itemsPerPage));
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (companyFilter !== 'all') params.set('companyId', companyFilter);
      if (statusFilter === 'all') params.set('isActive', 'all');
      else if (statusFilter === 'active') params.set('isActive', 'true');
      else if (statusFilter === 'inactive') params.set('isActive', 'false');

      const response = await api.get(`/clientes-fornecedores?${params.toString()}`);
      return response.data;
    },
    retry: 1,
    placeholderData: (prev) => prev,
  });

  const listPayload = (clientesData as any)?.data;
  const serverItems: any[] = Array.isArray(listPayload?.items)
    ? listPayload.items
    : Array.isArray(listPayload)
      ? listPayload
      : [];
  const pagination = listPayload?.pagination ?? {
    page: currentPage,
    limit: itemsPerPage,
    total: serverItems.length,
    totalPages: 1,
  };

  // Ordenação + filtros de coluna só na página atual (já veio paginada do servidor)
  const paginatedData = useMemo(() => {
    let filtered = serverItems.filter((item: any) => {
      const matchesCodigo =
        columnFilters.codigo === '' ||
        safeString(item.codigo, '').toLowerCase().includes(columnFilters.codigo.toLowerCase());
      const matchesNome =
        columnFilters.nome === '' ||
        safeString(item.nome, '').toLowerCase().includes(columnFilters.nome.toLowerCase());
      const matchesDescricao =
        columnFilters.descricao === '' ||
        safeString(item.descricao, '')
          .toLowerCase()
          .includes(columnFilters.descricao.toLowerCase());
      const matchesEmpresa =
        columnFilters.empresa === '' ||
        safeString(item.company?.name, '')
          .toLowerCase()
          .includes(columnFilters.empresa.toLowerCase());
      const matchesStatusCol =
        columnFilters.status === 'all' ||
        (columnFilters.status === 'active' && item.isActive) ||
        (columnFilters.status === 'inactive' && !item.isActive);
      return (
        matchesCodigo && matchesNome && matchesDescricao && matchesEmpresa && matchesStatusCol
      );
    });

    filtered = [...filtered].sort((a: any, b: any) => {
      let aValue = '';
      let bValue = '';
      switch (sortField) {
        case 'codigo':
          aValue = safeString(a.codigo, '');
          bValue = safeString(b.codigo, '');
          break;
        case 'descricao':
          aValue = safeString(a.descricao, '');
          bValue = safeString(b.descricao, '');
          break;
        case 'empresa':
          aValue = safeString(a.company?.name, '');
          bValue = safeString(b.company?.name, '');
          break;
        case 'status':
          aValue = a.isActive ? 'Ativo' : 'Inativo';
          bValue = b.isActive ? 'Ativo' : 'Inativo';
          break;
        default:
          aValue = safeString(a.nome, '');
          bValue = safeString(b.nome, '');
      }
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

    return filtered;
  }, [serverItems, columnFilters, sortField, sortDirection]);

  const totalPages = pagination.totalPages || 1;
  const totalItems = pagination.total ?? 0;

  // Ao buscar pela barra superior: se houver exatamente um resultado, abrir o modal do cliente
  useEffect(() => {
    const q = searchParams.get('search');
    if (!q?.trim() || paginatedData.length !== 1 || autoOpenedSearchRef.current === q.trim()) return;
    const one = paginatedData[0];
    if (one?.id && totalItems === 1) {
      autoOpenedSearchRef.current = q.trim();
      setSelectedCliente(one as ClienteFornecedorWithDetails);
      setIsClienteModalOpen(true);
    }
  }, [searchParams, paginatedData, totalItems]);

  const handleRefresh = () => {
    refetch();
    toast.success('Dados atualizados!');
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setStatusFilter('all');
    setCompanyFilter('all');
    clearColumnFilters();
    setCurrentPage(1);
  };

  const handleEdit = (_id: string) => {
    toast.success('Funcionalidade de edição em desenvolvimento');
  };

  const handleDelete = (_id: string) => {
    toast.success('Funcionalidade de exclusão em desenvolvimento');
  };

  // Função para ordenação
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Função para filtros por coluna
  const handleColumnFilter = (field: string, value: string) => {
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Função para limpar filtros de coluna
  const clearColumnFilters = () => {
    setColumnFilters({
      codigo: '',
      nome: '',
      descricao: '',
      empresa: '',
      status: 'all',
    });
  };

  const handleOpenClienteModal = (cliente: any) => {
    setSelectedCliente(cliente as ClienteFornecedorWithDetails);
    setIsClienteModalOpen(true);
  };

  const handleCloseClienteModal = () => {
    setIsClienteModalOpen(false);
    setSelectedCliente(null);
  };

  if (error) {
    return (
      <AuthErrorState
        error={error as Error}
        onRetry={handleRefresh}
      />
    );
  }

  if (isLoading && !clientesData) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p>Carregando {pageTitle}...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* <Helmet>
        <title>Clientes/Fornecedores - PortalFly Zeh Motoca</title>
      </Helmet> */}

      <div className="p-6 space-y-6" data-testid="page-clientes-fornecedores">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">{pageTitle}</h1>
            <p className="text-muted-foreground">
              {pageSubtitle}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRefresh} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            {!isOperador && (
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Novo Cliente/Fornecedor
            </Button>
            )}
          </div>
        </div>

        {/* Filtros */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filtros
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
              </Button>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Busca */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Buscar</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Nome, código ou descrição..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select
                    value={statusFilter}
                    onValueChange={setStatusFilter}
                  >
                    <option value="all">Todos</option>
                    <option value="active">Ativos</option>
                    <option value="inactive">Inativos</option>
                  </Select>
                </div>

                {/* Empresa */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Empresa</label>
                  <Select
                    value={companyFilter}
                    onValueChange={setCompanyFilter}
                  >
                    <option value="all">Todas</option>
                    {companies.map((company: { id: string; name: string }) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={handleClearFilters}>
                  Limpar Filtros
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Tabela */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{isOperador ? 'Lista de clientes' : 'Lista de Clientes/Fornecedores'}</CardTitle>
              <div className="flex items-center gap-4">
                <div className="text-sm text-muted-foreground">
                  {totalItems.toLocaleString('pt-BR')} registros
                  {isFetching ? ' · atualizando…' : ''}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted-foreground">Por página:</label>
                  <Select
                    value={itemsPerPage.toString()}
                    onValueChange={(value) => {
                      setItemsPerPage(Number(value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </Select>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    {/* Cabeçalho Código */}
                    <th className="text-left p-3 font-medium">
                      <div className="space-y-2">
                        <button
                          onClick={() => handleSort('codigo')}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Código
                          {sortField === 'codigo' && (
                            <span className="text-xs">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                        <Input
                          placeholder="Filtrar código..."
                          value={columnFilters.codigo}
                          onChange={(e) => handleColumnFilter('codigo', e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                    </th>

                    {/* Cabeçalho Nome */}
                    <th className="text-left p-3 font-medium">
                      <div className="space-y-2">
                        <button
                          onClick={() => handleSort('nome')}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Nome
                          {sortField === 'nome' && (
                            <span className="text-xs">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                        <Input
                          placeholder="Filtrar nome..."
                          value={columnFilters.nome}
                          onChange={(e) => handleColumnFilter('nome', e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                    </th>

                    {/* Cabeçalho Descrição */}
                    <th className="text-left p-3 font-medium">
                      <div className="space-y-2">
                        <button
                          onClick={() => handleSort('descricao')}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Descrição
                          {sortField === 'descricao' && (
                            <span className="text-xs">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                        <Input
                          placeholder="Filtrar descrição..."
                          value={columnFilters.descricao}
                          onChange={(e) => handleColumnFilter('descricao', e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                    </th>

                    {/* Cabeçalho Empresa */}
                    <th className="text-left p-3 font-medium">
                      <div className="space-y-2">
                        <button
                          onClick={() => handleSort('empresa')}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Empresa
                          {sortField === 'empresa' && (
                            <span className="text-xs">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                        <Input
                          placeholder="Filtrar empresa..."
                          value={columnFilters.empresa}
                          onChange={(e) => handleColumnFilter('empresa', e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                    </th>

                    {/* Cabeçalho Status */}
                    <th className="text-left p-3 font-medium">
                      <div className="space-y-2">
                        <button
                          onClick={() => handleSort('status')}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Status
                          {sortField === 'status' && (
                            <span className="text-xs">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                        <Select
                          value={columnFilters.status}
                          onValueChange={(value) => handleColumnFilter('status', value)}
                          className="h-8 text-xs"
                        >
                          <option value="all">Todos</option>
                          <option value="active">Ativos</option>
                          <option value="inactive">Inativos</option>
                        </Select>
                      </div>
                    </th>

                    <th className="text-left p-3 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center p-8 text-muted-foreground">
                        {searchTerm || statusFilter !== 'all' || companyFilter !== 'all' || 
                         Object.values(columnFilters).some(filter => filter !== '' && filter !== 'all')
                          ? 'Nenhum resultado encontrado para os filtros aplicados'
                          : 'Nenhum cliente/fornecedor encontrado'
                        }
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((item: any) => (
                      <tr key={item.id} className="border-b hover:bg-muted/50">
                        <td className="p-3">
                          <div className="font-mono text-sm">
                            {safeString(item.codigo, 'N/A')}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">
                              {safeString(item.nome, 'Sem nome')}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="max-w-xs truncate text-sm text-muted-foreground">
                            {safeString(item.descricao, 'Sem descrição')}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">
                              {safeString(item.company?.name, safeString(item.companyId, 'N/A'))}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant={item.isActive ? "default" : "secondary"}>
                            {item.isActive ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenClienteModal(item)}
                              title="Ver cliente (funil, etapa, status e títulos)"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(item.id)}
                              title="Editar"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(item.id)}
                              title="Excluir"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Página {currentPage} de {totalPages} · {totalItems.toLocaleString('pt-BR')} registros
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    Primeira
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="w-8 h-8 p-0"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Última
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Resumo da página atual */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Total (filtro)</p>
                  <p className="text-2xl font-bold">{totalItems.toLocaleString('pt-BR')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="w-5 h-5 p-0 flex items-center justify-center">
                  ✓
                </Badge>
                <div>
                  <p className="text-sm text-muted-foreground">Ativos nesta página</p>
                  <p className="text-2xl font-bold">
                    {paginatedData.filter((item: any) => item.isActive).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="w-5 h-5 p-0 flex items-center justify-center">
                  ✗
                </Badge>
                <div>
                  <p className="text-sm text-muted-foreground">Inativos nesta página</p>
                  <p className="text-2xl font-bold">
                    {paginatedData.filter((item: any) => !item.isActive).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Modal do cliente: funil, etapa, status da locação e títulos */}
        {selectedCliente && (
          <ClienteFornecedorModal
            cliente={selectedCliente}
            isOpen={isClienteModalOpen}
            onClose={handleCloseClienteModal}
          />
        )}
      </div>
    </>
  );
};
